/**
 * <package-picker> — "Choose Your Package" bundle selector.
 * Rendered by snippets/package-picker.liquid (main-product block "package_picker").
 * Behaviour mirrors codepen liamoates/LEWLqXQ, wired to the Shopify cart.
 */
if (!customElements.get('package-picker')) {
  customElements.define(
    'package-picker',
    class PackagePicker extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onStickyClick = this.onStickyClick.bind(this);
      }

      connectedCallback() {
        const readJson = (selector, fallback) => {
          try {
            const el = this.querySelector(selector);
            return el ? JSON.parse(el.textContent) : fallback;
          } catch (e) {
            return fallback;
          }
        };

        this.sectionId = this.dataset.section;
        this.tiers = readJson('[data-pp-tiers]', []).filter((t) => t.enabled);
        this.variants = readJson('[data-pp-variants]', []);
        this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
        this.skipCart = this.dataset.skipCart === 'true';
        this.propertyName = (this.dataset.propertyName || '').trim();
        this.hijackSticky = this.dataset.hijackSticky !== 'false';

        this.variant = this.getInitialVariant();
        this.kaching = null;
        if (this.dataset.pricingSource === 'kaching') this.applyKaching();
        const defaultTier = this.kaching && this.kaching.defaultIndex ? this.kaching.defaultIndex : Number(this.dataset.defaultTier || 0);
        this.tier = this.tiers.find((t) => t.index === defaultTier) || this.tiers[0] || null;

        this.elements = {
          tiles: Array.from(this.querySelectorAll('[data-pp-tier]')),
          atc: this.querySelector('[data-pp-atc]'),
          atcLabel: this.querySelector('[data-pp-atc-label]'),
          atcTotal: this.querySelector('[data-pp-atc-total]'),
          headTotal: this.querySelector('[data-pp-head-total]'),
          sale: this.querySelector('[data-pp-sale]'),
          shipDate: this.querySelector('[data-pp-ship-date]'),
          giftCount: this.querySelector('[data-pp-gift-count]'),
          spinner: this.querySelector('.pp__spinner'),
          error: this.querySelector('[data-pp-error]'),
          stickyButton: document.getElementById(`StickyAtcSubmitButton-${this.sectionId}`),
          stickyPrice: document.getElementById(`atc-price-${this.sectionId}`),
        };
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.addEventListener('click', this.onClick);
        if (this.hijackSticky && this.elements.stickyButton) {
          this.elements.stickyButton.addEventListener('click', this.onStickyClick, true);
        }
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const data = event && event.data ? event.data : {};
            if (data.sectionId && data.sectionId !== this.sectionId) return;
            const v = data.variant && data.variant.id ? this.variants.find((x) => x.id === data.variant.id) : null;
            if (v) {
              this.variant = v;
              this.render();
            }
          });
        }

        this.renderShipDate();
        this.render();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        if (this.elements && this.elements.stickyButton) {
          this.elements.stickyButton.removeEventListener('click', this.onStickyClick, true);
        }
        if (this.variantUnsubscriber) this.variantUnsubscriber();
        clearTimeout(this.addedTimer);
      }

      /* ---------- data ---------- */

      getInitialVariant() {
        const urlVariant = new URLSearchParams(window.location.search).get('variant');
        if (urlVariant) {
          const found = this.variants.find((v) => String(v.id) === String(urlVariant));
          if (found) return found;
        }
        return this.variants.find((v) => v.available) || this.variants[0] || null;
      }

      /** Variant id the theme's own form points at, when a variant picker block is on the page. */
      formVariantId() {
        const input = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
        return input && input.value ? Number(input.value) : null;
      }

      currentVariant() {
        const formId = this.formVariantId();
        if (formId) {
          const v = this.variants.find((x) => x.id === formId);
          if (v) return v;
        }
        return this.variant;
      }

      /**
       * Replace the theme-configured tiles with the product's Kaching deal (quantity breaks),
       * so quantities, prices and the preselected tier always follow the Kaching admin.
       * Falls back silently to the theme tiers when no Kaching deal is on the page.
       */
      applyKaching() {
        const K = window.LuxeKaching;
        if (!K) return;
        const deal = K.getDeal(this.dataset.productId);
        const bars = K.getQuantityBars(deal);
        const grid = this.querySelector('.pp__grid');
        const template = grid ? grid.querySelector('.pp__tier') : null;
        if (!bars.length || !template) return;

        this.tiers = bars.map((bar, i) => ({
          index: i + 1,
          enabled: true,
          qty: Number(bar.quantity),
          label: '',
          unit: null,
          total: null,
          pct: 0,
          fixed: 0,
          bar,
        }));

        const visible = Number(this.dataset.visibleCount) || this.tiers.length;
        const hasExpand = !!this.querySelector('[data-pp-expand]');
        grid.innerHTML = '';
        this.tiers.forEach((t) => {
          const tile = template.cloneNode(true);
          tile.dataset.ppTier = String(t.index);
          tile.dataset.ppQty = String(t.qty);
          tile.dataset.ppLabel = String(t.qty);
          tile.setAttribute('aria-pressed', 'false');
          tile.setAttribute('aria-label', `${t.qty} unit${t.qty > 1 ? 's' : ''}`);
          const qtyEl = tile.querySelector('.pp__qty');
          if (qtyEl) qtyEl.textContent = String(t.qty);
          const extra = hasExpand && t.index > visible;
          tile.toggleAttribute('data-pp-extra', extra);
          tile.hidden = extra;
          grid.appendChild(tile);
        });
        if (!hasExpand || this.tiers.length <= visible) {
          const expand = this.querySelector('[data-pp-expand]');
          if (expand) expand.hidden = true;
        }

        const pre = bars.findIndex((b) => b.id === deal.preselectedDealBarId);
        this.kaching = { deal, config: K.getConfig(), defaultIndex: pre >= 0 ? pre + 1 : null };
      }

      unitPrice(tier) {
        if (tier.bar && this.kaching) return window.LuxeKaching.perItem(this.totalPrice(tier), tier.qty);
        if (tier.unit !== null && tier.unit !== undefined) return Math.max(0, Number(tier.unit));
        const v = this.currentVariant();
        const base = v ? Number(v.price) : 0;
        const pctLeft = (100 - Number(tier.pct || 0)) / 100;
        const raw = Math.max(0, base * pctLeft - Number(tier.fixed || 0));
        if (this.dataset.priceRound === 'x99' && (Number(tier.pct) > 0 || Number(tier.fixed) > 0)) {
          return Math.floor(raw / 100) * 100 + 99;
        }
        return Math.round(raw);
      }

      totalPrice(tier) {
        if (tier.bar && this.kaching) {
          const v = this.currentVariant();
          const price = v ? Number(v.price) : 0;
          return window.LuxeKaching.bundleTotal(tier.bar, Array(tier.qty).fill(price), this.kaching.deal, this.kaching.config);
        }
        if (tier.total !== null && tier.total !== undefined) return Math.max(0, Number(tier.total));
        return this.unitPrice(tier) * tier.qty;
      }

      /** Saving vs the first tier's unit price, as in the pen (vs full price when Kaching drives tiers). */
      percentOff(tier) {
        if (tier.bar && this.kaching) {
          const v = this.currentVariant();
          const full = v ? Number(v.price) * tier.qty : 0;
          return full ? Math.round((1 - this.totalPrice(tier) / full) * 100) : 0;
        }
        const first = this.tiers[0];
        if (!first) return 0;
        const base = this.unitPrice(first);
        if (!base) return 0;
        return Math.round((1 - this.unitPrice(tier) / base) * 100);
      }

      formatMoney(cents) {
        const format = this.moneyFormat;
        const value = Number(cents) / 100;
        const fixed = (n, d, thousands = ',', decimal = '.') => {
          const parts = n.toFixed(d).split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return parts.length > 1 ? parts.join(decimal) : parts[0];
        };
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        const type = match ? match[1] : 'amount';
        let out;
        switch (type) {
          case 'amount_no_decimals':
            out = fixed(value, 0);
            break;
          case 'amount_with_comma_separator':
            out = fixed(value, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            out = fixed(value, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            out = fixed(value, 2, "'", '.');
            break;
          case 'amount_no_decimals_with_space_separator':
            out = fixed(value, 0, ' ', '.');
            break;
          case 'amount_with_space_separator':
            out = fixed(value, 2, ' ', ',');
            break;
          default:
            out = fixed(value, 2);
        }
        return format.replace(/\{\{\s*\w+\s*\}\}/, out);
      }

      /* ---------- render ---------- */

      renderShipDate() {
        const el = this.elements.shipDate;
        if (!el) return;
        const days = Number(this.dataset.shipDays || 0);
        const skipWeekends = this.dataset.skipWeekends === 'true';
        const d = new Date();
        let added = 0;
        let guard = 0;
        while (added < days && guard < 400) {
          guard += 1;
          d.setDate(d.getDate() + 1);
          const day = d.getDay();
          if (!skipWeekends || (day !== 0 && day !== 6)) added += 1;
        }
        // Same formatting as the pen: "Oct 01, 2026"
        el.textContent = d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
      }

      render() {
        this.elements.tiles.forEach((tile) => {
          const idx = Number(tile.dataset.ppTier);
          const tier = this.tiers.find((t) => t.index === idx);
          tile.setAttribute('aria-pressed', String(!!(this.tier && this.tier.index === idx)));
          const unitEl = tile.querySelector('[data-pp-unit]');
          if (tier && unitEl) unitEl.textContent = this.formatMoney(this.unitPrice(tier));
        });

        const total = this.tier ? this.formatMoney(this.totalPrice(this.tier)) : '';
        if (this.elements.headTotal) this.elements.headTotal.textContent = total;
        if (this.elements.sale && this.tier) {
          const pct = this.percentOff(this.tier);
          const format = this.dataset.saleFormat || '[pct]% OFF';
          this.elements.sale.textContent = format.replace('[pct]', pct);
          this.elements.sale.hidden = pct <= 0;
        }

        if (this.elements.giftCount && this.tier) {
          const perItem = Math.max(1, Number(this.dataset.giftPerItem || 1));
          const n = this.tier.qty * perItem;
          const format = this.dataset.giftFormat || '+ [n] FREE Case[s]';
          this.elements.giftCount.textContent = format
            .replace(/\[n\]/g, n)
            .replace(/\[s\]/g, n > 1 ? 's' : '')
            .replace(/\[S\]/g, n > 1 ? 'S' : '');
        }

        const v = this.currentVariant();
        const soldOut = !(v && v.available);
        if (this.elements.atcLabel && !this.elements.atc.classList.contains('is-added')) {
          this.elements.atcLabel.textContent = soldOut
            ? this.dataset.soldOutLabel || 'Sold out'
            : this.dataset.buttonLabel || 'ADD TO CART';
        }
        if (this.elements.atcTotal) {
          this.elements.atcTotal.textContent = soldOut ? '' : total;
          const dot = this.querySelector('.pp__dot');
          if (dot) dot.hidden = soldOut || !this.tier;
        }
        if (this.elements.atc) this.elements.atc.setAttribute('aria-disabled', String(soldOut || !this.tier));

        if (this.elements.stickyPrice && this.tier && this.hijackSticky) {
          this.elements.stickyPrice.innerHTML = `<span class="pp-sticky-price">${total}</span>`;
        }
      }

      flashAdded() {
        const { atc, atcLabel } = this.elements;
        if (!atc || !atcLabel) return;
        atcLabel.textContent = this.dataset.addedLabel || 'ADDED ✓';
        atc.classList.add('is-added');
        clearTimeout(this.addedTimer);
        this.addedTimer = setTimeout(() => {
          atc.classList.remove('is-added');
          this.render();
        }, 1400);
      }

      /* ---------- events ---------- */

      onClick(event) {
        const btn = event.target.closest('button');
        if (!btn || !this.contains(btn)) return;

        if (btn.dataset.ppTier !== undefined) {
          const idx = Number(btn.dataset.ppTier);
          this.tier = this.tiers.find((t) => t.index === idx) || this.tier;
          this.hideError();
          this.render();
          return;
        }

        if (btn.hasAttribute('data-pp-expand')) {
          this.querySelectorAll('[data-pp-extra]').forEach((tile) => {
            tile.hidden = false;
          });
          btn.setAttribute('aria-expanded', 'true');
          btn.hidden = true;
          return;
        }

        if (btn.hasAttribute('data-pp-atc')) {
          event.preventDefault();
          this.addToCart();
        }
      }

      onStickyClick(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.addToCart();
      }

      /* ---------- cart ---------- */

      buildItems() {
        const v = this.currentVariant();
        if (!v || !this.tier) return [];
        const item = { id: v.id, quantity: this.tier.qty };
        if (this.propertyName) {
          const tile = this.elements.tiles.find((t) => Number(t.dataset.ppTier) === this.tier.index);
          item.properties = { [this.propertyName]: tile ? tile.dataset.ppLabel : String(this.tier.qty) };
        }
        const items = [item];
        const giftVariant = Number(this.dataset.giftVariant || 0);
        if (giftVariant) {
          const perItem = Math.max(1, Number(this.dataset.giftPerItem || 1));
          items.push({ id: giftVariant, quantity: this.tier.qty * perItem });
        }
        return items;
      }

      setLoading(on) {
        const { atc, spinner, stickyButton } = this.elements;
        if (atc) {
          atc.classList.toggle('is-loading', on);
          atc.toggleAttribute('disabled', on);
        }
        if (spinner) spinner.hidden = !on;
        if (stickyButton) {
          stickyButton.classList.toggle('loading', on);
          const s = stickyButton.querySelector('.loading-overlay__spinner');
          if (s) s.classList.toggle('hidden', !on);
        }
      }

      showError(message) {
        if (!this.elements.error) return;
        this.elements.error.textContent = message;
        this.elements.error.hidden = false;
      }

      hideError() {
        if (this.elements.error) this.elements.error.hidden = true;
      }

      addToCart() {
        if (!this.tier || this.elements.atc.getAttribute('aria-disabled') === 'true' || this.elements.atc.disabled) return;
        const items = this.buildItems();
        if (!items.length) return;

        this.hideError();
        this.setLoading(true);

        const body = { items };
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          body.sections = this.cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
          if (typeof this.cart.setActiveElement === 'function') this.cart.setActiveElement(document.activeElement);
        }

        const url = (window.routes && window.routes.cart_add_url) || '/cart/add';
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then((r) => r.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message || 'Could not add to cart.');
              return;
            }
            if (this.skipCart) {
              window.location = '/checkout';
              return;
            }
            this.flashAdded();
            if (!this.cart) {
              window.location = (window.routes && window.routes.cart_url) || '/cart';
              return;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'package-picker', cartData: response });
            }
            const first = Array.isArray(response.items) ? response.items[0] : null;
            const parsed = first ? Object.assign({}, first, response) : response;
            this.cart.classList.remove('is-empty');
            this.cart.renderContents(parsed);
          })
          .catch((err) => {
            console.error(err);
            this.showError((window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.');
          })
          .finally(() => this.setLoading(false));
      }
    }
  );
}
